import { nextTick, onBeforeUnmount, ref, watch, type Ref } from 'vue'

// Publishes a layout's sticky header height as --header-height on its scroll container, and marks
// that container with data-sticky-header, and re-measures on resize. `isLoading` re-measures once
// data finishes loading (the header's content, and therefore its height, changes at that point).
//
// Scroll anchors under the header are offset by a single global rule in styles/main.scss keyed on
// [data-sticky-header], so adopting this composable needs no per-view CSS.
//
// Deliberately does NOT set scroll-padding-top on the scroll container: that offsets every
// scroll-into-view in the container, including the ones the browser performs itself to reveal a
// text selection or a focused element, which then land at the very top of the page.
export const useStickyHeader = (isLoading: Ref<boolean>) => {
  const stickyHeaderEl = ref<HTMLElement>()

  let resizeObserver: ResizeObserver | null = null
  let scrollContainer: HTMLElement | null = null
  let previousHeaderHeight: string | null = null

  const updateHeaderHeight = () => {
    const el = stickyHeaderEl.value
    if (!el) return
    const main = el.closest('main')
    if (!(main instanceof HTMLElement)) return

    if (scrollContainer !== main) {
      scrollContainer = main
      previousHeaderHeight = main.style.getPropertyValue('--header-height')
    }

    main.dataset.stickyHeader = ''
    main.style.setProperty('--header-height', `${el.offsetHeight}px`)
  }

  const restoreHeaderHeight = () => {
    if (!scrollContainer) return

    delete scrollContainer.dataset.stickyHeader

    if (previousHeaderHeight) {
      scrollContainer.style.setProperty('--header-height', previousHeaderHeight)
    } else {
      scrollContainer.style.removeProperty('--header-height')
    }

    scrollContainer = null
    previousHeaderHeight = null
  }

  watch(isLoading, (loading) => {
    if (!loading) nextTick(updateHeaderHeight)
  })

  // Call once the header has real content to measure (e.g. after the initial data load).
  const observeHeader = () => {
    const el = stickyHeaderEl.value
    if (!el) return

    resizeObserver = new ResizeObserver(() => {
      updateHeaderHeight()
    })
    resizeObserver.observe(el)
    void nextTick(updateHeaderHeight)
  }

  onBeforeUnmount(() => {
    resizeObserver?.disconnect()
    resizeObserver = null
    restoreHeaderHeight()
  })

  return { stickyHeaderEl, observeHeader }
}
